/**
 * useTutorTrainingState — « Ta formation » : l'étape (la vidéo, le quiz, le résultat), les réponses, et
 * l'envoi du quiz.
 *
 * Port de `TutorTrainingScreen.js` :
 *  - « Passer le quiz » vide les réponses et ouvre le quiz ; « Repasser le quiz », après un résultat,
 *    relit d'abord la formation (le serveur remélange les choix) ;
 *  - « Valider mes réponses » envoie `submitTutorTrainingQuiz` (`step_answers`), mesuré
 *    (`training.quiz`) ; au succès, le résultat (rangé par le réducteur) ; un refus se dit au-dessus du
 *    bouton au lieu du toast de `useApiStatus` ;
 *  - « Revoir la vidéo » revient à la vidéo ; « Retour à l'accueil » → `menu` ;
 *  - la vidéo s'ouvre hors de l'app (`openExternalUrl`) : le lecteur intégré n'existe pas encore au
 *    design system (`<VideoEmbed>`, proposé).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useReducer } from "react"

import { tutorTrainingService } from "../../../../../../data/services/tutorTrainingService"
import type { TutorTrainingPhase } from "../../../../../../design-system/tutor"
import { toQuizAnswersPayload } from "../../../../adapters/tutor/Account/tutorTrainingAdapter"
import { type TutorTrainingViewModel } from "../../../../view-models/tutor/Account/tutorTrainingViewModel"
import { openExternalUrl } from "../../../hooks/openExternalUrl"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"

export const QUIZ_SUBMIT_FAILED = "Tes réponses n'ont pas pu être envoyées. Réessaie dans un instant."

export type TutorTrainingUiState = {
    step: "video" | "quiz" | "result"
    answers: Record<number, string>
    submitError: string | null
}

export type TutorTrainingUiAction =
    | { type: "START_QUIZ" }
    | { type: "ANSWER"; questionId: number; answer: string }
    | { type: "SUBMITTING" }
    | { type: "FAILED"; message: string }
    | { type: "RESULT" }
    | { type: "VIDEO" }

export const initialTutorTrainingUiState: TutorTrainingUiState = { step: "video", answers: {}, submitError: null }

function tutorTrainingUiReducer(state: TutorTrainingUiState, action: TutorTrainingUiAction): TutorTrainingUiState {
    switch (action.type) {
        case "START_QUIZ":
            return { step: "quiz", answers: {}, submitError: null }
        case "ANSWER":
            return { ...state, answers: { ...state.answers, [action.questionId]: action.answer } }
        case "SUBMITTING":
            return { ...state, submitError: null }
        case "FAILED":
            return { ...state, submitError: action.message }
        case "RESULT":
            return { ...state, step: "result", submitError: null }
        case "VIDEO":
            return { ...state, step: "video", submitError: null }
        default:
            return state
    }
}

/** L'étape que la page montre : un résultat absent du store (relu entre-temps) ramène à la vidéo. */
function trainingPhase(
    ui: TutorTrainingUiState,
    vm: Pick<TutorTrainingViewModel, "passed" | "result">,
    submitting: boolean
): TutorTrainingPhase {
    if (ui.step === "quiz") return { kind: "quiz", answers: ui.answers, submitting, submitError: ui.submitError }
    if (ui.step === "result" && vm.result != null) return { kind: "result", ...vm.result }
    return { kind: "video", passed: vm.passed }
}

type StackNavigation = { navigate: (route: string) => void }

export function useTutorTrainingState(
    vm: Pick<TutorTrainingViewModel, "passed" | "result" | "stepId" | "videoUrl">,
    refetch: () => void
) {
    const navigation = useNavigation() as unknown as StackNavigation
    const onBack = useBackOr("menu")
    const [ui, dispatchUi] = useReducer(tutorTrainingUiReducer, initialTutorTrainingUiState)

    const { start, requesting } = useApiMutation({
        service: tutorTrainingService.submitTutorTrainingQuiz,
        gesture: "training.quiz",
        surface: "tutor_training",
        fallback: QUIZ_SUBMIT_FAILED,
        skipWhilePending: true,
        onSuccess: () => dispatchUi({ type: "RESULT" }),
        onError: (message) => dispatchUi({ type: "FAILED", message }),
    })

    const onSubmit = useCallback(() => {
        if (vm.stepId == null) return
        dispatchUi({ type: "SUBMITTING" })
        start({ id: vm.stepId }, toQuizAnswersPayload(ui.answers))
    }, [vm.stepId, ui.answers, start])

    return {
        phase: trainingPhase(ui, vm, requesting),
        onBack,
        onPlayVideo: () => openExternalUrl(vm.videoUrl),
        onStartQuiz: () => {
            if (ui.step === "result") refetch()
            dispatchUi({ type: "START_QUIZ" })
        },
        onSelectAnswer: (questionId: number, answer: string) => dispatchUi({ type: "ANSWER", questionId, answer }),
        onSubmit,
        onReviewVideo: () => dispatchUi({ type: "VIDEO" }),
        onDone: () => navigation.navigate("menu"),
    }
}
